const SAFE_REDIRECT_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Resolves a user-supplied redirect target into an absolute URL that is safe to navigate to.
 * Relative paths are resolved against `baseUrl`. Only http(s) URLs whose origin is the one of
 * `baseUrl` or one of `allowedOrigins` are accepted, which rules out `javascript:`, `data:` and
 * friends as well as open redirects to third-party sites.
 * @param redirectUrl The untrusted redirect target, already URL-decoded.
 * @param baseUrl The URL relative targets are resolved against. Its origin is always allowed.
 * @param allowedOrigins Extra URLs whose origins are allowed as redirect targets.
 * @return The resolved absolute URL, or null when the target is not allowed.
 */
export function resolveSafeRedirectUrl(
	redirectUrl: string,
	baseUrl: string,
	allowedOrigins: string[] = []
): string | null {
	const trimmedRedirectUrl = redirectUrl.trim();
	// An empty target would resolve to the base URL itself
	if (trimmedRedirectUrl === '') {
		return null;
	}

	try {
		const base = new URL(baseUrl);
		const target = new URL(trimmedRedirectUrl, base);

		if (!SAFE_REDIRECT_PROTOCOLS.has(target.protocol)) {
			return null;
		}

		const origins = new Set([base.origin]);
		for (const allowed of allowedOrigins) {
			// URL.canParse is not available in older WebViews (e.g. iOS < 17)
			try {
				origins.add(new URL(allowed).origin);
			} catch {
				// Invalid allowed origins are ignored
			}
		}

		return origins.has(target.origin) ? target.href : null;
	} catch {
		return null;
	}
}
